import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { User } from "../types/models";
import { onApiEvent } from "../services/api";
import * as authApi from "../services/auth";

type AuthContextValue = {
  user: User | null;
  loading: boolean;
  refreshSession: (options?: authApi.GetCurrentUserOptions) => Promise<User | null>;
  login: (email: string, password: string, totpCode?: string) => Promise<User | null>;
  loginWithPasskey: () => Promise<User | null>;
  register: (email: string, password: string, displayName?: string) => Promise<User | null>;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const refreshSession = useCallback(async (options: authApi.GetCurrentUserOptions = {}) => {
    setLoading(true);
    try {
      const currentUser = await authApi.getCurrentUser(options);
      setUser(currentUser);
      return currentUser;
    } catch (error) {
      if (options.requireProfile) {
        throw error;
      }
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refreshSession();
  }, [refreshSession]);

  useEffect(() => {
    return onApiEvent("unauthorized", () => {
      setUser(null);
    });
  }, []);

  const login = useCallback(async (email: string, password: string, totpCode?: string) => {
    setLoading(true);
    try {
      const nextUser = await authApi.login({ email, password, totp_code: totpCode || undefined });
      setUser(nextUser);
      return nextUser;
    } finally {
      setLoading(false);
    }
  }, []);

  const loginWithPasskey = useCallback(async () => {
    setLoading(true);
    try {
      const nextUser = await authApi.loginWithPasskey();
      setUser(nextUser);
      return nextUser;
    } finally {
      setLoading(false);
    }
  }, []);

  const register = useCallback(async (email: string, password: string, displayName?: string) => {
    setLoading(true);
    try {
      const nextUser = await authApi.register({ email, password, display_name: displayName || undefined });
      setUser(nextUser);
      return nextUser;
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    await authApi.logout();
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, loading, refreshSession, login, loginWithPasskey, register, logout }),
    [user, loading, refreshSession, login, loginWithPasskey, register, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) {
    throw new Error("useAuth must be used inside AuthProvider");
  }
  return value;
}
